// src/components/Footer.tsx

import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  Link,
} from "react-router-dom";

import "./Footer.css";


// =====================================================
// GAME GUIDES
// =====================================================

const GAME_GUIDES = [
  {
    name: "Fasoor",
    path: "/guides/fasoor",
  },
  {
    name: "Tika",
    path: "/guides/tika",
  },
  {
    name: "Bitruno",
    path: "/guides/bitruno",
  },
  {
    name: "Charwali",
    path: "/guides/charwali",
  },
  {
    name: "Fiskoot",
    path: "/guides/fiskoot",
  },
];


// =====================================================
// FOOTER
// =====================================================

const Footer: React.FC = () => {

  const year =
    new Date().getFullYear();

  const [
    guidesOpen,
    setGuidesOpen,
  ] = useState(false);

  const guideMenuRef =
    useRef<HTMLDivElement | null>(
      null
    );


  // ===================================================
  // CLOSE DROPDOWN WHEN CLICKING OUTSIDE
  // ===================================================

  useEffect(() => {

    const handleOutsideClick = (
      event: MouseEvent
    ) => {

      if (
        guideMenuRef.current &&
        !guideMenuRef.current.contains(
          event.target as Node
        )
      ) {

        setGuidesOpen(
          false
        );
      }
    };


    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );


    return () => {

      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };

  }, []);


  // ===================================================
  // UI
  // ===================================================

  return (
    <footer className="site-footer">

      <div className="footer-content">

        {/* ========================================= */}
        {/* BRAND                                     */}
        {/* ========================================= */}

        <div className="footer-brand">

          <h2>
            P<span>-ARENA</span>
          </h2>

          <p>
            Your destination for social card games.
            Play with friends, challenge online players,
            or compete against AI.
          </p>

        </div>


        {/* ========================================= */}
        {/* PLATFORM                                  */}
        {/* ========================================= */}

        <div className="footer-column">

          <h3>
            Platform
          </h3>

          <Link to="/">
            Game House
          </Link>

          <Link to="/about">
            About P-ARENA
          </Link>

          <Link to="/how-it-works">
            How It Works
          </Link>

        </div>


        {/* ========================================= */}
        {/* GAMES                                     */}
        {/* ========================================= */}

        <div className="footer-column">

          <h3>
            Games
          </h3>


          {/* ======================================= */}
          {/* GAME GUIDES DROPDOWN                    */}
          {/* ======================================= */}

          <div
            ref={guideMenuRef}
            className={`
              footer-guides
              ${
                guidesOpen
                  ? "open"
                  : ""
              }
            `}
          >

            <button
              type="button"
              className="footer-guides-trigger"
              aria-expanded={guidesOpen}
              aria-haspopup="menu"
              onClick={() =>
                setGuidesOpen(
                  (current) =>
                    !current
                )
              }
            >

              <span>
                Game Guides
              </span>

              <span
                className="footer-guides-chevron"
                aria-hidden="true"
              >
                ▾
              </span>

            </button>


            {/* ===================================== */}
            {/* DROPDOWN                              */}
            {/* ===================================== */}

            <div
              className="footer-guides-menu"
              role="menu"
            >

              <div className="footer-guides-menu-header">

                <span>
                  P-ARENA
                </span>

                <strong>
                  Game Guides
                </strong>

              </div>


              {/* =================================== */}
              {/* GUIDE LINKS                         */}
              {/* =================================== */}

              <div className="footer-guides-list">

                {GAME_GUIDES.map(
                  (guide) => (

                    <Link
                      key={guide.name}
                      to={guide.path}
                      role="menuitem"
                      className="footer-guide-link"
                      onClick={() =>
                        setGuidesOpen(
                          false
                        )
                      }
                    >

                      <span
                        className="footer-guide-suit"
                        aria-hidden="true"
                      >
                        ♣
                      </span>

                      <span className="footer-guide-name">
                        {guide.name}
                      </span>

                      <span
                        className="footer-guide-arrow"
                        aria-hidden="true"
                      >
                        ›
                      </span>

                    </Link>

                  )
                )}

              </div>

            </div>

          </div>


          {/* ======================================= */}
          {/* GAME RULES                              */}
          {/* ======================================= */}

          <Link to="/game-rules">
            Game Rules
          </Link>


          {/* ======================================= */}
          {/* ALL GAMES                               */}
          {/* ======================================= */}
          {/*
            Home.tsx already contains the complete
            P-ARENA game library.

            view=all tells Home.tsx to show all
            games instead of only featured games.

            #games points to the game section.
          */}

          <Link to="/?view=all#games">
            All Games
          </Link>

        </div>


        {/* ========================================= */}
        {/* SUPPORT                                   */}
        {/* ========================================= */}

        <div className="footer-column">

          <h3>
            Support
          </h3>

          <Link to="/help">
            Help Center
          </Link>

          <Link to="/contact">
            Contact Us
          </Link>

          <Link to="/report">
            Report a Problem
          </Link>

        </div>


        {/* ========================================= */}
        {/* LEGAL                                     */}
        {/* ========================================= */}

        <div className="footer-column">

          <h3>
            Legal
          </h3>

          <Link to="/privacy">
            Privacy Policy
          </Link>

          <Link to="/terms">
            Terms &amp; Conditions
          </Link>

          <Link to="/cookies">
            Cookie Policy
          </Link>

        </div>

      </div>


      {/* =========================================== */}
      {/* DIVIDER                                     */}
      {/* =========================================== */}

      <div className="footer-divider" />


      {/* =========================================== */}
      {/* FOOTER BOTTOM                               */}
      {/* =========================================== */}

      <div className="footer-bottom">

        <p className="footer-copyright">
          © {year} P-ARENA. All rights reserved.
        </p>


        {/* ========================================= */}
        {/* POWERED BY                                */}
        {/* ========================================= */}

        <p className="footer-powered-by">

          Powered by{" "}

          <a
            href="https://www.perouzsoftwaresolutions.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Perouz Software Solutions
          </a>

        </p>


        <p className="footer-tagline">
          Play • Connect • Compete
        </p>

      </div>

    </footer>
  );
};


export default Footer;